import { FC, useEffect, useMemo, useState } from "react";
import classes from './payment.module.scss'
import { MyInput } from "../../../shared/ui/input";
import { MyButton } from "../../../shared/ui/button";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { IPaymentLegalDocument, IPaymentLegalInfo, studentService } from "../../../entities/student";
import { MyCheckbox } from "../../../shared/ui/myCheckbox/myCheckbox";
import { MyError } from "../../../shared/lib/error/MyError";


interface IProps {
    hash: string;
}

const openLegalDocument = (document?: IPaymentLegalDocument) => {
    if(!document){
        return
    }

    if(document.source === 'url' && document.url){
        window.open(document.url, '_blank', 'noopener,noreferrer')
        return
    }

    if(document.source === 'base64' && document.content_base64){
        const binary = atob(document.content_base64)
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
        const blob = new Blob([bytes], {
            type: document.mime_type || 'application/octet-stream',
        })
        const objectUrl = URL.createObjectURL(blob)

        window.open(objectUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }
}

export const Payment: FC<IProps> = ({hash}) => {
    
    const [amount, setAmount] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [legalInfo, setLegalInfo] = useState<IPaymentLegalInfo | null>(null)
    const [isLegalInfoLoading, setIsLegalInfoLoading] = useState<boolean>(true)
    const [isOfferAccepted, setIsOfferAccepted] = useState<boolean>(false)
    const [isPersonalDataAccepted, setIsPersonalDataAccepted] = useState<boolean>(false)
    const [isSubmitAttempted, setIsSubmitAttempted] = useState<boolean>(false)
    
    const {setGlobalMessage} = useGlobalMessageActions()

    const shouldShowOfferCheckbox = Boolean(legalInfo?.offer_document)
    const shouldShowPersonalDataCheckbox = Boolean(
        legalInfo?.privacy_policy_document || legalInfo?.personal_data_consent_document
    )
    const shouldShowLegalCheckboxes = shouldShowOfferCheckbox || shouldShowPersonalDataCheckbox

    const legalError = useMemo(() => {
        if(!shouldShowLegalCheckboxes){
            return ''
        }

        if(shouldShowOfferCheckbox && !isOfferAccepted){
            return 'Подтвердите ознакомление с офертой'
        }

        if(shouldShowPersonalDataCheckbox && !isPersonalDataAccepted){
            return 'Подтвердите согласие с обработкой персональных данных'
        }

        return ''
    }, [isOfferAccepted, isPersonalDataAccepted, shouldShowLegalCheckboxes, shouldShowOfferCheckbox, shouldShowPersonalDataCheckbox])

    const getLegalInfo = async () => {
        try{
            setIsLegalInfoLoading(true)
            const data = await studentService.getPaymentLegalInfo(hash)
            setLegalInfo(data)
        }
        catch(e){
            if(e instanceof MyError && e.status === 404){
                setLegalInfo(null)
                return
            }

            console.log(e)
        }
        finally{
            setIsLegalInfoLoading(false)
        }
    }

    useEffect(() => {
        getLegalInfo()
    }, [hash])

    const onPayment = async () => {
        setIsSubmitAttempted(true)
        if(!amount){
            setError('Введите сумму')
            return
        }
        if(legalError){
            setError(legalError)
            return
        }
        try{
            setIsLoading(true)
            setError('')
            await studentService.payment(hash, amount, {
                is_offer_accepted: isOfferAccepted,
                is_privacy_policy_accepted: isPersonalDataAccepted,
                is_personal_data_consent_given: isPersonalDataAccepted,
            })
        }
        catch(e){
            if(e instanceof Error){
                setGlobalMessage({type: 'error', message: e.message})
            }
            else{
                setGlobalMessage({type: 'error', message: 'Ошибка оплаты'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    return (
        <section className={classes.container}>
            {legalInfo?.balance && (
                <section className={classes.summary}>
                    <section className={classes.balance}>
                        Баланс: {legalInfo.balance}
                    </section>
                </section>
            )}
            <section className={classes.content}>
                <section className={classes.title}>
                    Введите сумму оплаты
                </section>
                <section className={classes.fn}>
                    <MyInput 
                        value={amount} 
                        setValue={setAmount} 
                        type="number" 
                        setError={setError}
                        isLoading={isLoading}
                    />
                </section>
                {
                    !isLegalInfoLoading && shouldShowLegalCheckboxes &&
                    <section className={classes.legalInfo}>
                        {
                            shouldShowOfferCheckbox &&
                            <MyCheckbox
                                checked={isOfferAccepted}
                                onSelected={(value) => {
                                    setIsOfferAccepted(value)
                                    setError('')
                                }}
                                checkBoxTop
                            >
                                <span>
                                    Я ознакомлен (-а) и согласен (-на) с{' '}
                                    <a
                                        href={legalInfo?.offer_document?.source === 'url'
                                            ? legalInfo.offer_document.url
                                            : 'about:blank'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.legalLink}
                                        onClick={(event) => {
                                            event.preventDefault()
                                            openLegalDocument(legalInfo?.offer_document)
                                        }}
                                    >
                                        офертой
                                    </a>
                                </span>
                            </MyCheckbox>
                        }
                        {
                            shouldShowPersonalDataCheckbox &&
                            <MyCheckbox
                                checked={isPersonalDataAccepted}
                                onSelected={(value) => {
                                    setIsPersonalDataAccepted(value)
                                    setError('')
                                }}
                                checkBoxTop
                            >
                                <span>
                                    Я ознакомлен (-а) с{' '}
                                    {
                                        legalInfo?.privacy_policy_document
                                            ?
                                        <a
                                            href={legalInfo.privacy_policy_document.source === 'url'
                                                ? legalInfo.privacy_policy_document.url
                                                : 'about:blank'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.legalLink}
                                            onClick={(event) => {
                                                event.preventDefault()
                                                openLegalDocument(legalInfo.privacy_policy_document)
                                            }}
                                        >
                                            политикой в отношении персональных данных
                                        </a>
                                            :
                                        'политикой в отношении персональных данных'
                                    }
                                    {' '}и даю{' '}
                                    {
                                        legalInfo?.personal_data_consent_document
                                            ?
                                        <a
                                            href={legalInfo.personal_data_consent_document.source === 'url'
                                                ? legalInfo.personal_data_consent_document.url
                                                : 'about:blank'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.legalLink}
                                            onClick={(event) => {
                                                event.preventDefault()
                                                openLegalDocument(legalInfo.personal_data_consent_document)
                                            }}
                                        >
                                            согласие на обработку персональных данных
                                        </a>
                                            :
                                        'согласие на обработку персональных данных'
                                    }
                                    .
                                </span>
                            </MyCheckbox>
                        }
                    </section>
                }
                <section className={classes.fn}>
                    <MyButton 
                        onClick={onPayment}
                        error={error || (isSubmitAttempted ? legalError : '')}
                        isLoading={isLoading}
                    >
                        Оплатить
                    </MyButton>
                </section>
            </section>
        </section>
    )
}
